import { COMPASS_LOOKS, resolveColour, type CompassKind, type CompassShape } from "odyssey-shared";

/**
 * The four pip shapes of ADR-061's amendment of 2026-10-14 (item 7), as original 5 by 5 art, a `#` for a pixel of the
 * core. The same art is the pin's mark in the field and the pin's pip on the band, so the two read as one thing. The
 * teardrop's tip is the bottom middle pixel, which is where the pin stands in the field.
 */
export const COMPASS_SHAPES: Readonly<Record<CompassShape, readonly string[]>> = Object.freeze({
  teardrop: Object.freeze([".###.", "#####", "#####", ".###.", "..#.."]),
  star: Object.freeze(["..#..", "#####", ".###.", ".#.#.", "#...#"]),
  square: Object.freeze(["#####", "#####", "#####", "#####", "#####"]),
  diamond: Object.freeze(["..#..", ".###.", "#####", ".###.", "..#.."]),
});

/**
 * The side of a shape's core in pixels.
 */
export const COMPASS_SHAPE_SIZE = 5;

/**
 * The part of a 2D context a shape is painted with.
 */
export interface ShapeContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect(x: number, y: number, width: number, height: number): void;
}

/**
 * One shape's pixels, found once: the cells of its core and the cells of its 1 px outline, each as an offset from the
 * core's top left (the outline's reach to -1 and 5).
 */
interface ShapeCells {
  readonly core: readonly (readonly [number, number])[];
  readonly outline: readonly (readonly [number, number])[];
}

/**
 * Finds a shape's core cells and the cells of the 1 px outline round them, eight-connected.
 *
 * @param rows The shape's rows.
 * @returns The two lists.
 */
function cellsOf(rows: readonly string[]): ShapeCells {
  const core: [number, number][] = [];
  const outline: [number, number][] = [];
  const filled = (x: number, y: number): boolean => rows[y]?.[x] === "#";
  for (let y = -1; y <= COMPASS_SHAPE_SIZE; y += 1) {
    for (let x = -1; x <= COMPASS_SHAPE_SIZE; x += 1) {
      if (filled(x, y)) {
        core.push([x, y]);
      } else if (filled(x - 1, y - 1) || filled(x, y - 1) || filled(x + 1, y - 1) || filled(x - 1, y) || filled(x + 1, y) || filled(x - 1, y + 1) || filled(x, y + 1) || filled(x + 1, y + 1)) {
        outline.push([x, y]);
      }
    }
  }
  return { core, outline };
}

const CELLS: Readonly<Record<CompassShape, ShapeCells>> = Object.freeze({
  teardrop: cellsOf(COMPASS_SHAPES.teardrop),
  star: cellsOf(COMPASS_SHAPES.star),
  square: cellsOf(COMPASS_SHAPES.square),
  diamond: cellsOf(COMPASS_SHAPES.diamond),
});

/**
 * The colour a kind's pip is filled with: its single token, or for a party member the token of the roster place.
 *
 * @param kind The kind.
 * @param place The roster place, 0 to 5, read only for a party member.
 * @returns The colour, as the palette resolves it.
 */
export function pipFill(kind: CompassKind, place: number): string {
  const tokens = COMPASS_LOOKS[kind].tokens;
  return resolveColour(tokens[kind === "party" ? place : 0] ?? tokens[0] ?? "text");
}

/**
 * Paints a shape: its 1 px outline in `outline` and then its core in `fill`, with the core's top left at a pixel.
 *
 * @param context The 2D context.
 * @param shape The shape.
 * @param left The core's left pixel.
 * @param top The core's top pixel.
 * @param fill The core's colour.
 * @param outline The outline's colour.
 */
export function paintShape(context: ShapeContext, shape: CompassShape, left: number, top: number, fill: string, outline: string): void {
  const cells = CELLS[shape];
  context.fillStyle = outline;
  for (const [x, y] of cells.outline) {
    context.fillRect(left + x, top + y, 1, 1);
  }
  context.fillStyle = fill;
  for (const [x, y] of cells.core) {
    context.fillRect(left + x, top + y, 1, 1);
  }
}
